import { Source, Layer } from 'react-map-gl/mapbox'
import type { PointsOfInterestResponse } from '../../../../types'
import { useCallback } from 'react'
import { getGeoJSONPoints, getGeoJSONPolygons } from '../../util'
import type { MapboxMapHook } from '@/components/_SiteWide/MapboxMap/hooks/useMapboxMap'
import { getMarkerLayout, getPoiCirclePaint, getPolygonFillPaint, getPolygonOutlinePaint } from './constants'
import type { GeoJSONFeature } from 'mapbox-gl'

type PointsOfInterestLayerProps = {
  pointsOfInterest: PointsOfInterestResponse
  mapboxMapHook: MapboxMapHook
  hoveredId?: string
}

export function PointsOfInterestLayer({ pointsOfInterest, mapboxMapHook, hoveredId }: PointsOfInterestLayerProps) {
  const { isStyleLoaded, showLayerPopover, hideLayerPopover, flyToLocation, useLayer, mapSettings } = mapboxMapHook

  const themeColor1 = getComputedStyle(document.documentElement).getPropertyValue('--theme-color-1').trim()
  const themeColor2 = getComputedStyle(document.documentElement).getPropertyValue('--theme-color-2').trim()
  const themeColor3 = getComputedStyle(document.documentElement).getPropertyValue('--theme-color-3').trim()

  const handlePoiClick = useCallback(
    (feature: GeoJSONFeature | null) => {
      if (!feature || !feature.geometry) return
      if ('coordinates' in feature.geometry) {
        const coordinates = feature.geometry?.coordinates
        if (coordinates && coordinates.length >= 2) {
          const [lon, lat] = coordinates as [number, number]
          flyToLocation([lon, lat])
        }
      }
    },
    [flyToLocation]
  )
  useLayer('sites-dots', { onMouseEnter: showLayerPopover, onMouseLeave: hideLayerPopover, onClick: handlePoiClick })
  useLayer('terminals-dots', {
    onMouseEnter: showLayerPopover,
    onMouseLeave: hideLayerPopover,
    onClick: handlePoiClick,
  })
  useLayer('depots-dots', { onMouseEnter: showLayerPopover, onMouseLeave: hideLayerPopover, onClick: handlePoiClick })
  useLayer('sites-markers', { onMouseEnter: showLayerPopover, onMouseLeave: hideLayerPopover, onClick: handlePoiClick })
  useLayer('terminals-markers', {
    onMouseEnter: showLayerPopover,
    onMouseLeave: hideLayerPopover,
    onClick: handlePoiClick,
  })
  useLayer('depots-markers', {
    onMouseEnter: showLayerPopover,
    onMouseLeave: hideLayerPopover,
    onClick: handlePoiClick,
  })

  if (!isStyleLoaded) return null

  return (
    <>
      {Object.entries(pointsOfInterest).map(([key, locations]) => {
        if (locations.length === 0) return null

        const geoJSONPolygons = getGeoJSONPolygons(locations)
        const geoJSONPoints = getGeoJSONPoints(locations)
        const color = key === 'depots' ? themeColor1 : key === 'terminals' ? themeColor2 : themeColor3

        return (
          <div key={key}>
            <Source key={`${key}-source`} id={`${key}-source`} type='geojson' data={geoJSONPolygons}>
              <Layer id={`${key}-polygon-fill`} minzoom={12} type='fill' paint={getPolygonFillPaint(color)} />
              <Layer id={`${key}-polygon-outline`} minzoom={12} type='line' paint={getPolygonOutlinePaint(color)} />
            </Source>
            <Source key={`${key}-point-source`} id={`${key}-point-source`} type='geojson' data={geoJSONPoints}>
              <Layer
                id={`${key}-dots`}
                type='circle'
                maxzoom={10} // Hide at zoom level 10 and below
                paint={getPoiCirclePaint(color, hoveredId, key === 'depots', mapSettings.style)}
              />
              <Layer
                id={`${key}-markers`}
                minzoom={10} // Hide at zoom level 10 and above
                type='symbol'
                layout={getMarkerLayout(key, hoveredId)}
              />
            </Source>
          </div>
        )
      })}
    </>
  )
}
